import { Autocomplete, AutocompleteOption, ListItemContent, ListItemDecorator } from '@mui/joy';
import classnames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { type ContractorSkillOption } from '@/types/trpc/team';

import { trpc } from '@/config/trpc';
import { useAutocompleteOptions } from '@/shared/hooks/autocomplete';

type BaseProps = {
  disabled?: boolean;
  onBlur?: () => void;
  onlyShowActive?: boolean;
  excludedSkillIds?: string[];
  disableClearable?: boolean;
};

type SingleProps = BaseProps & {
  callback: (value: ContractorSkillOption | null) => void;
  multiple?: false;
  selected: string | null;
};

type MultiProps = BaseProps & {
  callback: (value: ContractorSkillOption[]) => void;
  multiple: true;
  selected: string[];
};

type Props = SingleProps | MultiProps;

const SelectContractorSkillDefinition: React.FC<Props> = ({
  callback,
  disabled,
  multiple,
  onBlur,
  selected,
  onlyShowActive = false,
  excludedSkillIds = [],
  disableClearable = false,
}) => {
  const query = trpc.team.contractorSkill.autocomplete.useQuery();

  const autocompleteProps = useAutocompleteOptions({
    ...query,
    callback: (newOptions) => {
      const multipleOptions = Array.isArray(newOptions);

      if (multiple && multipleOptions) callback(newOptions);
      if (!multiple && !multipleOptions) callback(newOptions);
    },
    data:
      query.data?.filter(
        (skill) => (!onlyShowActive || !!skill.active) && !excludedSkillIds.includes(skill.id),
      ) || [],
    disabled,
    multiple,
    selected,
  });

  return (
    <Autocomplete
      {...autocompleteProps}
      disableClearable={disableClearable}
      getOptionLabel={(option) => option.combinedLabel ?? option.label}
      getOptionDisabled={(option) => !option.active}
      onBlur={onBlur}
      renderOption={(props, option) => (
        <AutocompleteOption {...props} key={option.value}>
          {option.depth > 1 && (
            <ListItemDecorator style={{ marginLeft: `${option.depth * 10}px` }}>
              <FontAwesomeIcon icon={['far', 'arrow-turn-down-right']} />
            </ListItemDecorator>
          )}
          <ListItemContent className={classnames(option.depth === 1 && 'font-medium')}>
            {option.label}
          </ListItemContent>
        </AutocompleteOption>
      )}
    />
  );
};

export default SelectContractorSkillDefinition;
